import { _axios } from "@/plugins/axios";

export interface MenuItem {
  nombre: string;
  tipo: number;
  url?: string;
  nombre_web?: string;
  blank?: string;
  sub_menu?: MenuItem[];
}

interface MenuCache {
  items: MenuItem[];
  ts: number;
}

/**
 * Servicio del menú de navegación del sitio.
 *
 * El menú se administra desde el admin (Menús → Menú del sitio) y se obtiene del API
 * (GET /api/menu). Si el API no responde o devuelve un árbol inválido, se usa el archivo
 * estático `new_menu_maipu.json` como respaldo, de modo que el sitio nunca deje de mostrar
 * su navegación. El contrato entregado a los componentes es el mismo que el del JSON.
 */
class MenuService {
  private static readonly TTL_MS = 5 * 60 * 1000;
  private static readonly STORAGE_KEY = "menu_sitio_cache_v1";

  private memoria: MenuCache | null = null;
  private enVuelo: Promise<MenuItem[]> | null = null;

  async getMenu(): Promise<MenuItem[]> {
    const cacheado = this.obtenerDeCache();
    if (cacheado) {
      return cacheado;
    }

    if (!this.enVuelo) {
      this.enVuelo = this.cargar()
        .then((items: MenuItem[]) => {
          this.guardarEnCache(items);
          this.enVuelo = null;
          return items;
        })
        .catch(() => {
          this.enVuelo = null;
          return [] as MenuItem[];
        });
    }

    return this.enVuelo;
  }

  private async cargar(): Promise<MenuItem[]> {
    try {
      const items = await this.desdeApi();
      if (this.esValido(items)) {
        return items;
      }
      console.warn("[menu] El API no devolvió un menú válido; se usa el menú estático");
    } catch (error) {
      console.warn("[menu] API no disponible; se usa el menú estático", error);
    }

    return this.desdeEstatico();
  }

  private async desdeApi(): Promise<MenuItem[]> {
    const response: any = await _axios.get("/menu");
    return Array.isArray(response.data) ? response.data : [];
  }

  private async desdeEstatico(): Promise<MenuItem[]> {
    const response = await fetch("/new_menu_maipu.json");
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  private esValido(items: any): items is MenuItem[] {
    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    return items.every(
      (item: any) =>
        item &&
        typeof item.nombre === "string" &&
        (item.sub_menu === undefined || Array.isArray(item.sub_menu))
    );
  }

  private obtenerDeCache(): MenuItem[] | null {
    if (this.memoria && Date.now() - this.memoria.ts < MenuService.TTL_MS) {
      return this.memoria.items;
    }

    try {
      const crudo = window.sessionStorage.getItem(MenuService.STORAGE_KEY);
      if (!crudo) {
        return null;
      }

      const cache: MenuCache = JSON.parse(crudo);
      if (cache && cache.ts && Date.now() - cache.ts < MenuService.TTL_MS && this.esValido(cache.items)) {
        this.memoria = cache;
        return cache.items;
      }
    } catch (error) {
      // cache corrupta: se ignora
    }

    return null;
  }

  private guardarEnCache(items: MenuItem[]): void {
    this.memoria = { items, ts: Date.now() };

    try {
      window.sessionStorage.setItem(MenuService.STORAGE_KEY, JSON.stringify(this.memoria));
    } catch (error) {
      // sessionStorage no disponible: se mantiene solo la cache en memoria
    }
  }
}

export default new MenuService();
